import { observe, computeSelector, symbolUndef, isPrimitive, isObservable, getNode, ObservablePrimitiveClass, extraPrimitiveProps, computed, observable, isFunction } from '@legendapp/state';
import { useReducer, useRef, forwardRef, memo, useCallback, createElement, Children, useMemo, useEffect } from 'react';
import { useObservable as useObservable$1 } from '@legendapp/state/react';

const Update$1 = (s) => s + 1;
function useSelector(selector, options) {
    var _a;
    let inRun = true;
    let ret = symbolUndef;
    const forceRender = (options === null || options === void 0 ? void 0 : options.forceRender) || useReducer(Update$1, 0)[1];
    const shouldRender = options === null || options === void 0 ? void 0 : options.shouldRender;
    const refDispose = useRef();
    (_a = refDispose.current) === null || _a === void 0 ? void 0 : _a.call(refDispose);
    if (!selector)
        return selector;
    refDispose.current = observe(function update(e) {
        // If running, call selector and re-render if changed
        let cur = (inRun || shouldRender !== true) && computeSelector(selector);
        // Re-render if not currently rendering and value has changed
        if (!inRun &&
            (shouldRender === true || (shouldRender ? shouldRender(cur, ret) : cur !== ret || !isPrimitive(cur)))) {
            forceRender();
            // Set cancel so that observe does not track
            e.cancel = true;
        }
        ret = cur;
        inRun = false;
    });
    // Note: This does not have a useEffect to cleanup listeners because it is ok
    // to call useReducer after unmounting. So it will lazily cleanup after unmount
    // because it will call forceRender() and return false to not track. Then since forceRender() does
    // not trigger re-render since it's unmounted, it does not set up tracking again.
    return ret;
}

const Update = (s) => s + 1;
// Extracting the forwardRef inspired by https://github.com/mobxjs/mobx/blob/main/packages/mobx-react-lite/src/observer.ts
const hasSymbol =  typeof Symbol === 'function' && Symbol.for;
function createReactiveComponent(component, observe, reactive, bindKeys) {
    const ReactForwardRefSymbol = hasSymbol
        ? Symbol.for('react.forward_ref')
        : typeof forwardRef === 'function' && forwardRef((props) => null)['$$typeof'];
    // If this component is already reactive bail out early
    // This can happen with Fast Refresh.
    if (component['__legend_proxied'])
        return component;
    let useForwardRef = false;
    let render = component;
    // Unwrap forwardRef on the component
    if (ReactForwardRefSymbol && component['$$typeof'] === ReactForwardRefSymbol) {
        useForwardRef = true;
        render = component['render'];
        if (process.env.NODE_ENV === 'development' && typeof render !== 'function') {
            throw new Error(`[legend-state] \`render\` property of ForwardRef was not a function`);
        }
    }
    const proxyHandler = {
        apply(target, thisArg, argArray) {
            const fr = useReducer(Update, 0)[1];
            // If this is a reactive component, convert all props ending in $
            // to regular props and set up a useSelector listener
            if (reactive) {
                const props = argArray[0];
                const propsOut = {};
                const keys = Object.keys(props);
                for (let i = 0; i < keys.length; i++) {
                    const key = keys[i];
                    const p = props[key];
                    // Convert reactive props
                    if (key.endsWith('$')) {
                        const k = key.slice(0, -1);
                        // Return raw value and Listen to the selector for changes
                        propsOut[k] = useSelector(p, { forceRender: fr });
                        // If this key is one of the bind keys set up a two-way binding
                        const bind = bindKeys === null || bindKeys === void 0 ? void 0 : bindKeys[k];
                        if (bind && isObservable(p)) {
                            // Use the bind's defaultValue if value is undefined
                            if (bind.defaultValue !== undefined && propsOut[k] === undefined) {
                                propsOut[k] = bind.defaultValue;
                            }
                            // Hook up the change lander
                            propsOut[bind.handler] = useCallback((e) => {
                                var _a;
                                p.set(bind.getValue(e));
                                // @ts-ignore
                                (_a = props[bind.handler]) === null || _a === void 0 ? void 0 : _a.call(props, e);
                            }, [props[bind.handler], bindKeys]);
                        }
                        // Delete the reactive key
                        delete propsOut[key];
                    }
                    else if (propsOut[key] === undefined) {
                        propsOut[key] = p;
                    }
                }
                argArray[0] = propsOut;
            }
            // If observing wrap the whole render in a useSelector to listen to it
            if (observe && fr) {
                return useSelector(() => Reflect.apply(target, thisArg, argArray), {
                    forceRender: fr,
                    shouldRender: true,
                });
            }
            else {
                return Reflect.apply(target, thisArg, argArray);
            }
        },
    };
    const proxy = new Proxy(render, proxyHandler);
    let ret;
    if (useForwardRef) {
        ret = forwardRef(proxy);
        ret['__legend_proxied'] = true;
    }
    else {
        ret = proxy;
    }
    return observe ? memo(ret) : ret;
}
function observer(component) {
    return createReactiveComponent(component, true);
}
function reactive(component, bindKeys) {
    return createReactiveComponent(component, false, true, bindKeys);
}
function reactiveObserver(component, bindKeys) {
    return createReactiveComponent(component, true, true, bindKeys);
}

let isEnabled = false;
function enableLegendStateReact() {
    if (!isEnabled) {
        isEnabled = true;
        // Rendering observables directly inspired by Preact Signals: https://github.com/preactjs/signals/blob/main/packages/react/src/index.ts
        // Add the extra primitive props so that observables can render directly
        // Memoized component to wrap the observable value
        const Text = memo(function Text({ data }) {
            const value = useSelector(data);
            const root = getNode(data).root;
            if (root.activate) {
                root.activate();
                root.activate = undefined;
            }
            return value;
        });
        const ReactTypeofSymbol = hasSymbol ? Symbol.for('react.element') : createElement('a').$$typeof;
        const s = extraPrimitiveProps;
        // Set extra props for the proxyHandler to return on primitives
        s.set(Symbol.toPrimitive, (_, value) => value);
        s.set('props', {
            __fn: (obs) => ({ data: obs }),
        });
        s.set('$$typeof', ReactTypeofSymbol);
        s.set('type', Text);
        s.set('_store', { validated: true });
        s.set('key', null);
        s.set('ref', null);
        s.set('alternate', null);
        s.set('_owner', null);
        s.set('_source', null);
        const config = (value) => ({ configurable: true, value });
        // Set extra props for ObservablePrimitive to return on primitives
        Object.defineProperties(ObservablePrimitiveClass.prototype, {
            [Symbol.toPrimitive]: {
                configurable: true,
                get() {
                    return this.peek();
                },
            },
            props: {
                configurable: true,
                get() {
                    return { data: this };
                },
            },
            $$typeof: config(ReactTypeofSymbol),
            type: config(Text),
            _store: config({ validated: true }),
            ref: config(null),
            alternate: config(null),
            _owner: config(null),
            _source: config(null),
        });
    }
}

function Computed({ children }) {
    return useSelector(children, { shouldRender: true });
}
const Memo = /*#__PURE__*/ memo(function Memo({ children }) {
    return useSelector(children, { shouldRender: true });
}, () => true);
function Show({ if: if_, else: else_, wrap, children, }) {
    const value = useSelector(if_);
    const child = useSelector(value ? children : else_ ? else_ : null, { shouldRender: true });
    return wrap ? createElement(wrap, undefined, child) : child;
}
function Switch({ value, children, default: fallback, }) {
    var _a, _b, _c, _d, _e, _f;
    if (value !== undefined) {
        // If value then this is selecting from an object of cases
        return ((_f = (_c = (_b = (_a = children)[useSelector(value)]) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : (_e = (_d = children)['default']) === null || _e === void 0 ? void 0 : _e.call(_d)) !== null && _f !== void 0 ? _f : null);
    }
    else {
        // If no value then render the first Show child with a matching if prop
        const arr = Children.toArray(children);
        const index = useSelector(() => {
            for (let i = 0; i < arr.length; i++) {
                if (computeSelector(arr[i].props.if))
                    return i;
            }
            return -1;
        });
        return index >= 0 ? arr[index] : fallback !== null && fallback !== void 0 ? fallback : null;
    }
}
function For({ each, optimized, item, itemProps, children, }) {
    var _a;
    if (!each)
        return null;
    // Get the raw value with a shallow listener so this list only re-renders
    // when the array length changes
    const v = useSelector(() => each.get(optimized ? 'optimize' : true), {
        shouldRender: true,
    });
    // The child function gets wrapped in a memoized observer component
    if (!item && children) {
        // Update the ref so the generated component uses the latest function
        const refChildren = useRef();
        refChildren.current = children;
        item = useMemo(() => observer(({ item }) => refChildren.current(item)), []);
    }
    if (!v)
        return null;
    // Get the appropriate id field
    const id = v.length > 0 ? (v[0].id ? 'id' : v[0]._id ? '_id' : v[0].__id ? '__id' : undefined) : undefined;
    // Create the child elements
    let out = [];
    for (let i = 0; i < v.length; i++) {
        if (v[i]) {
            const key = (_a = v[i][id]) !== null && _a !== void 0 ? _a : i;
            out.push(createElement(item, Object.assign({ key: key, item: each[i] }, itemProps)));
        }
    }
    return out;
}

function useMount(fn) {
    return useEffect(fn, []);
}
function useUnmount(fn) {
    return useEffect(() => fn, []);
}

function useComputed(compute, deps) {
    const ref = useRef({});
    ref.current.compute = compute;
    return useMemo(() => computed(() => ref.current.compute()), deps || []);
}

function useIsMounted() {
    const obs = useObservable$1(false);
    useEffect(() => {
        obs.set(true);
        return () => {
            obs.set(false);
        };
    }, []);
    return obs;
}

/**
 * A React hook that creates a new observable
 *
 * @param initialValue The initial value of the observable or a function that returns the initial value
 *
 * @see https://www.legendapp.com/dev/state/react/#useObservable
 */
function useObservable(initialValue) {
    // Create the observable from the default value
    return useMemo(() => observable(isFunction(initialValue) ? initialValue() : initialValue), []);
}

function useObservableReducer(reducer, initializerArg, initializer) {
    const obs = useObservable(() => initializerArg !== undefined && isFunction(initializerArg) ? initializer(initializerArg) : initializerArg);
    const dispatch = (action) => {
        obs.set(reducer(obs.get(), action));
    };
    return [obs, dispatch];
}

function useObserve(selector, reaction) {
    var _a;
    const refDispose = useRef();
    (_a = refDispose.current) === null || _a === void 0 ? void 0 : _a.call(refDispose);
    refDispose.current = observe(selector, reaction);
    useEffect(() => {
        // React 18 StrictMode workaround to re-observe when useEffect is called twice
        if (!refDispose.current) {
            refDispose.current = observe(selector, reaction);
        }
        return () => {
            var _a;
            (_a = refDispose.current) === null || _a === void 0 ? void 0 : _a.call(refDispose);
            refDispose.current = undefined;
        };
    });
}

function useObserveEffect(selector, reaction) {
    const ref = useRef({ selector });
    ref.current = { selector, reaction };
    useEffect(() => observe(((e) => {
        const { selector } = ref.current;
        return isFunction(selector) ? selector(e) : selector;
    }), (e) => { var _a, _b; return (_b = (_a = ref.current).reaction) === null || _b === void 0 ? void 0 : _b.call(_a, e); }), []);
}

export { Computed, For, Memo, Show, Switch, enableLegendStateReact, hasSymbol, observer, reactive, reactiveObserver, useComputed, useIsMounted, useMount, useObservable, useObservableReducer, useObserve, useObserveEffect, useSelector, useUnmount };
